import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import * as api from './api';
import { stuurTeamsPotje } from './mail';
import { useAuth } from './auth';
import { START_RATING, replay, ranglijst as maakRanglijst } from './elo';
import type { Match, Player, Role, Rol, Status, Tournament } from './types';

/* ==========================================================================
   Server-state via TanStack Query.

   Eén sleutel per tabel. Alles wat schrijft, maakt daarna de betrokken
   sleutels ongeldig; de schermen halen zelf opnieuw op. Afgeleide waarden
   (ratings, ranglijst) worden hier berekend en niet apart bewaard — er is maar
   één bron van waarheid, en dat zijn de potjes.
   ========================================================================== */

export const sleutels = {
  players: ['players'] as const,
  matches: ['matches'] as const,
  tournaments: ['tournaments'] as const,
  profiles: ['profiles'] as const
};

/** Zonder inlog geeft de database niets terug; dan hoeven we niet te vragen. */
function useMagLezen(): boolean {
  const { ingelogd } = useAuth();
  return ingelogd;
}

export function usePlayers() {
  return useQuery({ queryKey: sleutels.players, queryFn: api.listPlayers, enabled: useMagLezen() });
}

export function useMatches(pouleId?: string | null) {
  return useQuery({
    queryKey: [...sleutels.matches, pouleId ?? 'alles'],
    queryFn: () => api.listMatches(pouleId),
    enabled: useMagLezen()
  });
}

export function useTournaments(pouleId?: string | null) {
  return useQuery({
    queryKey: [...sleutels.tournaments, pouleId ?? 'alles'],
    queryFn: () => api.listTournaments(pouleId),
    enabled: useMagLezen()
  });
}

export function useProfiles() {
  const { isAdmin, demo } = useAuth();
  return useQuery({ queryKey: sleutels.profiles, queryFn: api.listProfiles, enabled: isAdmin && !demo });
}

const GEEN_SPELERS: Player[] = [];
const GEEN_POTJES: Match[] = [];
const GEEN_TOERNOOIEN: Tournament[] = [];

/**
 * De competitie in één haak: spelers, potjes, toernooien en alles wat daaruit
 * volgt. Een speler die niet meer bestaat blijft toonbaar als schim, zodat
 * oude uitslagen leesbaar blijven.
 */
export function useLeague(pouleId?: string | null) {
  const spelers = usePlayers();
  const potjes = useMatches(pouleId);
  const toernooien = useTournaments(pouleId);

  const players = spelers.data ?? GEEN_SPELERS;
  const alleMatches = potjes.data ?? GEEN_POTJES;
  const tournaments = toernooien.data ?? GEEN_TOERNOOIEN;

  /*
   * Sinds migratie 015 telt een uitslag pas als beide kanten hem erkennen.
   * Alles wat rekent — Elo, ranglijst, statistiek — gaat daarom over `matches`,
   * en dat zijn uitsluitend de bevestigde. Wie de rest wil tonen (de historie,
   * het belletje) pakt `wachtend` of `alleMatches`.
   */
  /*
   * Eén filter, en daarmee is het overal geregeld: rating, ranglijst,
   * statistieken en prestaties werken allemaal op `matches`. Wie de rest wil
   * tonen — de historie — gebruikt `alleMatches`.
   */
  const matches = useMemo(
    () => alleMatches.filter((m) => m.status === 'bevestigd' && m.teltMee !== false),
    [alleMatches]
  );
  const wachtend = useMemo(() => alleMatches.filter((m) => m.status === 'wacht'), [alleMatches]);

  const elo = useMemo(() => replay(matches), [matches]);

  const zoekSpeler = useMemo(() => {
    const index = new Map(players.map((p) => [p.id, p]));
    return (id: string): Player =>
      index.get(id) ?? {
        id,
        name: 'Onbekende speler',
        username: null,
        bijnaam: null,
        bijnaamDoor: null,
        photo: '',
        isActive: false,
        createdAt: ''
      };
  }, [players]);

  const actief = useMemo(() => players.filter((p) => p.isActive), [players]);

  const ranking = useMemo(() => maakRanglijst(actief, matches, elo.ratings), [actief, matches, elo]);

  return {
    players,
    actief,
    matches,
    wachtend,
    alleMatches,
    tournaments,
    elo,
    ranking,
    zoekSpeler,
    rating: (id: string) => elo.ratings[id] ?? START_RATING,
    laden: spelers.isLoading || potjes.isLoading || toernooien.isLoading,
    fout: spelers.error ?? potjes.error ?? toernooien.error ?? null
  };
}

/* ---------- schrijven ---------- */

export function useRecordMatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.recordMatch,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: sleutels.matches });
      void qc.invalidateQueries({ queryKey: sleutels.players });
    }
  });
}

/** Akkoord geven; zodra beide kanten tekenden telt het potje mee. */
export function useBevestigUitslag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.bevestigUitslag,
    onSuccess: (uit) => {
      void qc.invalidateQueries({ queryKey: sleutels.matches });
      void qc.invalidateQueries({ queryKey: sleutels.players });
      /* Pas als beide kanten tekenden telt het potje mee; dán pas het kanaal in. */
      if (uit?.bevestigd) void stuurTeamsPotje(uit.id);
    }
  });
}

/** De stand rechtzetten; daarna moet de andere kant opnieuw tekenen. */
export function useWijzigUitslag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.wijzigUitslag,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: sleutels.matches });
      void qc.invalidateQueries({ queryKey: sleutels.players });
    }
  });
}

export function useDeleteMatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.deleteMatch,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: sleutels.matches });
      void qc.invalidateQueries({ queryKey: sleutels.players });
    }
  });
}

export function useCreatePlayer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ naam, foto }: { naam: string; foto?: File | null }) => api.createPlayer(naam, foto),
    onSuccess: () => void qc.invalidateQueries({ queryKey: sleutels.players })
  });
}

export function useUpdatePlayer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ speler, foto }: { speler: Player; foto?: File | null }) => api.updatePlayer(speler, foto),
    onSuccess: () => void qc.invalidateQueries({ queryKey: sleutels.players })
  });
}

export function useSaveTournament() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.saveTournament,
    onSuccess: () => void qc.invalidateQueries({ queryKey: sleutels.tournaments })
  });
}

export function useDeleteTournament() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ toernooi, potjes }: { toernooi: Tournament; potjes: Match[] }) =>
      api.deleteTournament(toernooi, potjes),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: sleutels.tournaments });
      void qc.invalidateQueries({ queryKey: sleutels.matches });
    }
  });
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      velden
    }: {
      id: string;
      velden: {
        name?: string;
        username?: string | null;
        role?: Role;
        status?: Status;
        playerId?: string | null;
      };
    }) => api.updateProfile(id, velden),
    onError: () => undefined,
    onSuccess: () => void qc.invalidateQueries({ queryKey: sleutels.profiles })
  });
}

/* ---------- rollen, permissies, logboek en gebruik ---------- */

export const sleutelsBeheer = {
  rollen: ['rollen'] as const,
  permissies: ['permissies'] as const,
  rolPermissies: ['rol-permissies'] as const,
  logboek: ['logboek'] as const,
  gebruik: ['gebruik'] as const,
  mail: ['mail'] as const
};

export function useRollen() {
  const { ingelogd } = useAuth();
  return useQuery({ queryKey: sleutelsBeheer.rollen, queryFn: api.listRollen, enabled: ingelogd });
}

export function usePermissies() {
  const { mag } = useAuth();
  return useQuery({
    queryKey: sleutelsBeheer.permissies,
    queryFn: api.listPermissies,
    enabled: mag('pagina.rollen')
  });
}

export function useRolPermissies() {
  const { mag } = useAuth();
  return useQuery({
    queryKey: sleutelsBeheer.rolPermissies,
    queryFn: api.listRolPermissies,
    enabled: mag('pagina.rollen')
  });
}

export function useBewaarRol() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ rol, nieuw }: { rol: Rol; nieuw: boolean }) => api.bewaarRol(rol, nieuw),
    onSuccess: () => void qc.invalidateQueries({ queryKey: sleutelsBeheer.rollen })
  });
}

export function useVerwijderRol() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.verwijderRol,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: sleutelsBeheer.rollen });
      void qc.invalidateQueries({ queryKey: sleutelsBeheer.rolPermissies });
    }
  });
}

export function useZetRolPermissies() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ rol, sleutels }: { rol: string; sleutels: string[] }) => api.zetRolPermissies(rol, sleutels),
    onSuccess: () => void qc.invalidateQueries({ queryKey: sleutelsBeheer.rolPermissies })
  });
}

export function useLogboek() {
  const { mag } = useAuth();
  return useQuery({
    queryKey: sleutelsBeheer.logboek,
    queryFn: () => api.listLogboek(300),
    enabled: mag('pagina.logs')
  });
}

export function useSchoonLogboekOp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.schoonLogboekOp,
    onSuccess: () => void qc.invalidateQueries({ queryKey: sleutelsBeheer.logboek })
  });
}

export function useGebruik(dagen: number) {
  const { mag } = useAuth();
  const vanaf = new Date(Date.now() - dagen * 86400000).toISOString().slice(0, 10);
  return useQuery({
    queryKey: [...sleutelsBeheer.gebruik, dagen],
    queryFn: () => api.listGebruik(vanaf),
    enabled: mag('pagina.gebruik')
  });
}

export function useMailVerzending(limiet = 25) {
  const { mag } = useAuth();
  return useQuery({
    queryKey: [...sleutelsBeheer.mail, limiet],
    queryFn: () => api.listMailVerzending(limiet),
    enabled: mag('pagina.gebruik')
  });
}

/** Het tegoed van vandaag; boven de limiet gaat er niets meer weg. */
export function useMailTegoed() {
  const { mag } = useAuth();
  return useQuery({
    queryKey: [...sleutelsBeheer.mail, 'tegoed'],
    queryFn: () => api.mailTegoed(),
    enabled: mag('pagina.gebruik')
  });
}

/* ---------- poules ---------- */

export const sleutelsPoule = {
  poules: ['poules'] as const,
  leden: ['poule-leden'] as const,
  uitnodigingen: ['poule-uitnodigingen'] as const
};

export function usePoules() {
  const { ingelogd, mag } = useAuth();
  return useQuery({
    queryKey: sleutelsPoule.poules,
    queryFn: api.listPoules,
    enabled: ingelogd && mag('pagina.poules')
  });
}

export function usePouleLeden() {
  const { ingelogd, mag } = useAuth();
  return useQuery({
    queryKey: sleutelsPoule.leden,
    queryFn: api.listPouleLeden,
    enabled: ingelogd && mag('pagina.poules')
  });
}

export function useUitnodigingen() {
  const { ingelogd } = useAuth();
  return useQuery({ queryKey: sleutelsPoule.uitnodigingen, queryFn: api.listUitnodigingen, enabled: ingelogd });
}

function verversPoules(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: sleutelsPoule.poules });
  void qc.invalidateQueries({ queryKey: sleutelsPoule.leden });
  void qc.invalidateQueries({ queryKey: sleutelsPoule.uitnodigingen });
}

export function useMaakPoule() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: api.maakPoule, onSuccess: () => verversPoules(qc) });
}

export function useWijzigPoule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      velden
    }: {
      id: string;
      velden: { naam?: string; omschrijving?: string | null; prijs?: string | null };
    }) => api.wijzigPoule(id, velden),
    onSuccess: () => verversPoules(qc)
  });
}

export function useVerwijderPoule() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: api.verwijderPoule, onSuccess: () => verversPoules(qc) });
}

export function useNodigUit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ pouleId, gebruikersnaam }: { pouleId: string; gebruikersnaam: string }) =>
      api.nodigUit(pouleId, gebruikersnaam),
    onSuccess: () => verversPoules(qc)
  });
}

export function useBeantwoordUitnodiging() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, akkoord }: { id: string; akkoord: boolean }) => api.beantwoordUitnodiging(id, akkoord),
    onSuccess: () => verversPoules(qc)
  });
}

export function useZetLidRol() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ pouleId, profielId, rol }: { pouleId: string; profielId: string; rol: 'beheerder' | 'lid' }) =>
      api.zetLidRol(pouleId, profielId, rol),
    onSuccess: () => verversPoules(qc)
  });
}

export function useVerwijderLid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ pouleId, profielId }: { pouleId: string; profielId: string }) =>
      api.verwijderLid(pouleId, profielId),
    onSuccess: () => verversPoules(qc)
  });
}

export function useZetBijnaam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ spelerId, bijnaam }: { spelerId: string; bijnaam: string }) =>
      api.zetBijnaam(spelerId, bijnaam),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: sleutels.players });
      void qc.invalidateQueries({ queryKey: sleutels.profiles });
    }
  });
}

export function useZetFavoriet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ pouleId, favoriet }: { pouleId: string; favoriet: boolean }) =>
      api.zetFavoriet(pouleId, favoriet),
    onSuccess: () => verversPoules(qc)
  });
}

export function usePouleRatings(pouleId: string | null) {
  const { ingelogd } = useAuth();
  return useQuery({
    queryKey: ['poule-ratings', pouleId],
    queryFn: () => api.listPouleRatings(pouleId as string),
    enabled: ingelogd && Boolean(pouleId)
  });
}
